import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import TierChip from './TierChip';
import EvidenceStrip from './EvidenceStrip';
import HiddenNote from './HiddenNote';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { pinDestination, reviewHasKey } from '../../stores/slices/reviewsSlice';
import { suggestionPinKey } from '../../stores/slices/pinsSlice';
import type { LadderSuggestion, PolicyBlock } from '../../apis';
import { stromaSectionLabel, stromaWalkCard } from '../LandingAgent/askPresentation';

const KIND_LABEL: Record<string, string> = { draft: 'draft', watch: 'watch', 'automate-later': 'automate later' };
const RISK_COLOR: Record<string, string> = { low: 'var(--ok)', medium: 'var(--warn)', high: 'var(--bad)' };

/** Enough digits to mean something: a rate's 0.02 is not 0.0. */
export const fmt = (v: number, unit?: string | null) => {
  const sign = v > 0 ? '+' : '';
  const s = v === 0 ? '0'
    : Math.abs(v) < 1 ? `${sign}${Number(v.toPrecision(2))}`
    : Math.abs(v) < 100 ? `${sign}${v.toFixed(1)}`
    : `${sign}${Math.round(v).toLocaleString()}`;
  return unit ? `${s} ${unit.replace(/_/g, ' ')}` : s;
};

/**
 * WHAT NEXT (#188): suggestions anchored to inspectable drivers. Each one
 * says what to do, what to expect (a projection computed from the number
 * itself, not narrated), which driver it rests on and how sure that driver
 * is. Nothing here spends money — a draft is carried out by a person.
 */
export default function NextCard({ suggestions, note, policy, threadId, onRaise, raising, readOnly = false, formatText = text => text,
  sessionId = null, restsOnReading = false }: {
  formatText?: (text: string) => string;
  readOnly?: boolean;
  /** The answer the suggestions came from, named to the review that takes one. */
  sessionId?: string | null;
  /** #1818: the answer rests on a reading of a meaning no person has settled: nothing of it goes into a review. */
  restsOnReading?: boolean;
  suggestions: LadderSuggestion[] | null | undefined;
  note?: string | null;
  policy?: PolicyBlock | null;
  threadId?: string | null;
  /** Raise the evidence bar for this answer (#187): suggestions below it are counted, not shown. */
  onRaise?: (bar: string) => void;
  raising?: boolean;
}) {
  const pinSuggestion = useVoyagerStore((s) => s.pinSuggestion);
  const pins = useVoyagerStore((s) => s.pins);
  // A living review takes the suggestion straight into its commentary (#189).
  const destination = useVoyagerStore((s) => pinDestination(s.savedThreads, s.currentThreadId,
    s.savedThreads.find((t) => t.id === threadId)?.perspectiveId || null));
  const list = (suggestions || []).filter((s) => s.kind !== 'follow_up');
  const withheld = policy?.policy === 'facts' && policy?.next_withheld;
  const belowBar = policy?.policy === 'gated' ? (policy?.next_hidden || 0) : 0;
  if (!list.length && !note && !withheld && !belowBar) return null;
  return (
    <Box data-testid="next-card" sx={{ mt: 1.5, ...stromaWalkCard, borderColor: 'divider' }}>
      <Typography variant="caption" sx={stromaSectionLabel}>What next</Typography>
      {withheld && <HiddenNote policy={policy} what="suggestions" />}
      {belowBar > 0 && <HiddenNote policy={policy} count={belowBar} what="suggestions" onRaise={onRaise} raising={raising} />}
      {!withheld && note && !list.length && (
        <Typography sx={{ fontSize: 13, mt: 0.5 }} data-testid="next-note">{note}</Typography>
      )}
      {list.map((s, i) => {
        const key = suggestionPinKey(s, threadId || null);
        const pinned = destination ? reviewHasKey(destination, key) : pins.some((p) => p.key === key);
        return (
          <Box key={`${key}:${i}`} data-testid="next-suggestion" data-kind={s.kind} sx={{ mt: 1, display: 'grid', gap: 0.5, pt: 1.25, borderTop: i ? '1px solid var(--line-soft)' : 0 }}>
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip size="small" label={KIND_LABEL[s.kind] || s.kind} sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, letterSpacing: 0.5 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{formatText(s.text)}</Typography>
              {s.risk && <Typography variant="caption" sx={{ color: RISK_COLOR[s.risk] || 'text.secondary' }}>risk {s.risk}</Typography>}
            </Box>
            {s.projection && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }} data-testid="next-projection" title={formatText(s.projection.basis)}>
                projected {fmt(s.projection.value, s.projection.unit)} · {formatText(s.projection.basis || '')}
              </Typography>
            )}
            {s.driver && (
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }} data-testid="next-driver">
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>driver:</Typography>
                <Chip size="small" variant="outlined" label={formatText(s.driver.label || s.driver.id || '')} sx={{ height: 20, fontSize: '0.68rem' }} />
                <TierChip tier={s.driver.tier} />
              </Box>
            )}
            <EvidenceStrip sources={s.sources} max={3} />
            {!readOnly && !restsOnReading && <Box>
              <Button size="small" variant="text" disabled={pinned} onClick={() => pinSuggestion(s, threadId || null, sessionId)} sx={{ py: 0, minHeight: 0, fontSize: '0.72rem' }}>
                {pinned ? (destination ? `In ${destination.title}` : 'In your review commentary') : (destination ? `Add to ${destination.title}` : 'Add to review commentary')}
              </Button>
            </Box>}
          </Box>
        );
      })}
    </Box>
  );
}
